import { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Select, MenuItem, InputLabel, FormControl, TextField, Checkbox, FormControlLabel, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useSelector } from 'react-redux';
import TEXT from './text';
import { fieldTypes } from '../../../config/fieldTypesConfig';
import { fieldSelector } from '../../../utils/fieldSelector';
import { theme } from '../../../theme';
import { useYjsDbGroups, useYjsDatabases } from '../../../services/yjs';

const DbCreator = ({ mode = 'dbgroup' }) => {
    // mode can be 'dbgroup' or 'db'
    const language = useSelector(state => state.App_Settings?.language || 'en');
    const userEmail = useSelector(state => state.UserMetaData?.email);
    const userUid = useSelector(state => state.UserMetaData?.uid);

    // Yjs custom hooks for real-time collaborative state & offline IndexedDB persistence
    const { 
        dbGroups: availableDbGroups, 
        createDbGroup 
    } = useYjsDbGroups({ uid: userUid });

    const { 
        createDatabase 
    } = useYjsDatabases({ uid: userUid });

    const [selectedDbGroup, setSelectedDbGroup] = useState(null);
    const [dbName, setDbName] = useState('');
    const [dbGroupName, setDbGroupName] = useState('');
    const [items, setItems] = useState([]);
    const [fieldName, setFieldName] = useState('');
    const [fieldType, setFieldType] = useState('');
    const [isMandatory, setIsMandatory] = useState(false);
    const [itemOptions, setItemOptions] = useState({});
    const [itemConstraints, setItemConstraints] = useState({});
    const [KeepItSimple, setKeepItSimple] = useState(true);
    const [dragDisabled, setDragDisabled] = useState(false); // State to enable/disable dragging
    const [nextItemId, setNextItemId] = useState(0);
    
    const get_item_by_id = (id) => {
        const item = items.find(item => item.id === id);
        console.log("Fetching item by ID:", id, "Result:", item);
        return item;
    };
    const get_item_index_by_id = (id) => {
        const index = items.findIndex(item => item.id === id);
        console.log("Fetching item index by ID:", id, "Result index:", index);
        return index;
    }

    const handleAddItem = () => {
        if (fieldName && fieldType) {
            const newItem = {
                id: `${nextItemId}`,
                name: fieldName,
                type: fieldType,
                mandatory: isMandatory,
                properties: { 
                    options: KeepItSimple 
                        ? { required: isMandatory, label: fieldName } 
                        : { required: isMandatory, label: fieldName, ...itemOptions }, 
                    constrains: KeepItSimple ? {} : itemConstraints, 
                    KeepItSimple 
                },
                value: null,
                valid: false,
            };
            console.log(`[${mode.toUpperCase()}] Adding new item:`, newItem);
            setItems([...items, newItem]);
            setNextItemId(nextItemId + 1);
            setFieldName('');
            setFieldType('');
            setIsMandatory(false);
            setItemOptions({});
            setItemConstraints({});
            setKeepItSimple(true);
        }
    };

    const handleRemoveItem = (index) => {
        console.log(`[${mode.toUpperCase()}] Removing item at index:`, index);
        const updatedItems = items.filter((_, i) => i !== index);
        setItems(updatedItems);
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        console.log(`[${mode.toUpperCase()}] Dragging item:`, result);
        
        // Check if the drag is happening within a Custom Note
        if (result.source.droppableId.startsWith('custom-note-droppable-')) {
            console.log(`[${mode.toUpperCase()}] Custom Note internal drag detected`);
            
            // Find the Custom Note item by extracting the ID from droppableId
            const customNoteId = result.source.droppableId.replace('custom-note-droppable-', '');
            const customNoteItem = items.find(item => item.id === customNoteId);
            
            if (customNoteItem && customNoteItem.value) {
                // Reorder the fields within the Custom Note
                const reorderedFields = Array.from(customNoteItem.value);
                const [removed] = reorderedFields.splice(result.source.index, 1);
                reorderedFields.splice(result.destination.index, 0, removed);
                
                // Update the items array with the reordered fields
                const updatedItems = items.map(item => 
                    item.id === customNoteId 
                        ? { ...item, value: reorderedFields }
                        : item
                );
                
                console.log(`[${mode.toUpperCase()}] Reordered Custom Note fields:`, reorderedFields);
                setItems(updatedItems);
            }
            return;
        }
        
        // Handle main items drag
        const reorderedItems = Array.from(items);
        const [removed] = reorderedItems.splice(result.source.index, 1);
        reorderedItems.splice(result.destination.index, 0, removed);

        setItems(reorderedItems);
    };

    const handleFieldClick = (field) => {
        console.log(`[${mode.toUpperCase()}] Field clicked:`, field);
        const fieldType = fieldTypes.find((type) => type.type === field.type);
        if ( fieldType?.defaultName) {
            console.log(`[${mode.toUpperCase()}] Default name for field type:`, fieldType.defaultName);
            setFieldName( fieldType.defaultName );
        }
    };

    const renderOptions = () => {
        if (!fieldType) return null;
        const selectedType = fieldTypes.find((type) => type.type === fieldType);

        return (
            <div style={{ marginTop: '10px' }}>
                <h4>Available Options</h4>
                <FormControlLabel
                    control={<Checkbox checked={KeepItSimple} onChange={(e) => setKeepItSimple(e.target.checked)} />}
                    label="Keep it simple"
                />
                <FormControlLabel
                    control={<Checkbox checked={isMandatory} onChange={(e) => setIsMandatory(e.target.checked)} />}
                    label="Mandatory"
                />
                {selectedType?.options &&
                    Object.entries(selectedType.options).map(([key, value]) => (
                        <FormControlLabel
                            disabled={KeepItSimple}
                            key={key}
                            control={<Checkbox checked={value} />}
                            label={key}
                        />
                    ))}
            </div>
        );
    };

    const renderConstraints = () => {
        if (!fieldType) return null;
        const selectedType = fieldTypes.find((type) => type.type === fieldType);
        if (!selectedType?.constrains) return null;

        return (
            <div style={{ marginTop: '10px' }}>
                <h4>Available Constraints</h4>
                <ul>
                    {Object.entries(selectedType.constrains).map(([key, value]) => (
                        <li key={key}><strong>{key}:</strong> {value.hint}</li>
                    ))}
                </ul>
            </div>
        );
    };

    const handleSelectDbGroup = (dbGroup) => {
        setSelectedDbGroup(dbGroup);
        if (!dbGroup) {
            setItems([]);
            return;
        }
        // Load the fields from selected DBGroup
        const rawFields = Array.isArray(dbGroup.fields) ? dbGroup.fields : [];
        const fields = rawFields.map(field => {
            // Parse Custom Note value back to array if needed
            if (field.type === 'Custom Note' && typeof field.value === 'string') {
                try {
                    return { ...field, value: JSON.parse(field.value) };
                } catch (e) {
                    return field;
                }
            }
            return field;
        });
        setItems(fields);
        console.log('[DB] Selected DBGroup:', dbGroup);
        console.log('[DB] Loaded fields:', fields);
    };

    const handleCreateDb = async () => {
        // Validate DB name
        if (!dbName || dbName.trim() === '') {
            alert('Please enter a name for the Database');
            return;
        }
        
        if (!selectedDbGroup) {
            alert('Please select a DBGroup template');
            return;
        }
        
        // Create the Database instance
        const database = {
            name: dbName.trim(),
            dbGroupId: selectedDbGroup.id,
            dbGroupName: selectedDbGroup.name,
            timestamp: new Date().toISOString(),
            records: [], // This will store actual data entries
            fields: selectedDbGroup.fields || [],
            totalFields: selectedDbGroup.totalFields || selectedDbGroup.fields?.length || 0,
            version: 1,
            storageType: 'local',
            owners: userEmail || '',
            admins: '',
            contributors: '',
            readers: '',
            supervised: '',
        };
        
        // Save using Yjs custom hook (window.Yjs.tables.<jsondb_name> will be initialized)
        try {
            const saved = await createDatabase(database);
            console.log('[DB] Database created via Yjs hook:', saved);

            alert(`✅ Database "${dbName}" created successfully!\n\nBased on DBGroup: "${selectedDbGroup.name}"\nFields: ${selectedDbGroup.totalFields || selectedDbGroup.fields?.length || 0}`);

            // Clear the form
            setDbName('');
        } catch (err) {
            console.error('[DB] Error creating Database via Yjs hook:', err);
            alert('❌ Error creating Database. Please try again.');
        }
    };

    const handleCreateDbGroup = async () => {
        // Validate DBGroup name
        if (!dbGroupName || dbGroupName.trim() === '') {
            alert('Please enter a name for the DBGroup');
            return;
        }

        // Create a deep copy of items
        const itemsCopy = JSON.parse(JSON.stringify(items));

        // Process Custom Note fields to convert their value to JSON string
        const processedItems = itemsCopy.map(item => {
            if (item.type === 'Custom Note' && item.value) {
                // Convert Custom Note value (array of fields) to JSON string
                return {
                    ...item,
                    value: JSON.stringify(item.value)
                };
            }
            return item;
        });

        // Create the DbGroup object
        const dbGroup = {
            name: dbGroupName.trim(),
            timestamp: new Date().toISOString(),
            totalFields: processedItems.length,
            fields: processedItems,
            version: 1,
            storageType: 'local',
            owners: userEmail || '',
            admins: '',
            contributors: '',
            readers: '',
            supervised: '',
        };

        // Save using Yjs custom hook
        try {
            const saved = await createDbGroup(dbGroup);
            console.log('[DBGROUP] DbGroup created via Yjs hook:', saved);

            alert(`✅ DBGroup Template Created!\n\nName: "${dbGroupName}"\nFields: ${processedItems.length}\n\nSynced & saved via Yjs!`);

            // Optionally clear the form
            setDbGroupName('');
            setItems([]);
        } catch (err) {
            console.error('[DBGROUP] Error creating DbGroup via Yjs hook:', err);
            alert('❌ Error saving DBGroup Template. Please try again.');
        }

        return dbGroup;
    };

    const handleExportDbGroupJSON = () => {
        // Validate DBGroup name
        if (!dbGroupName || dbGroupName.trim() === '') {
            alert('Please enter a name for the DBGroup');
            return;
        }
        
        // Create a deep copy of items
        const itemsCopy = JSON.parse(JSON.stringify(items));
        
        // Process Custom Note fields to convert their value to JSON string
        const processedItems = itemsCopy.map(item => {
            if (item.type === 'Custom Note' && item.value) {
                // Convert Custom Note value (array of fields) to JSON string
                return {
                    ...item,
                    value: JSON.stringify(item.value)
                };
            }
            return item;
        });
        
        // Create the DbGroup object
        const dbGroup = {
            name: dbGroupName.trim(),
            timestamp: new Date().toISOString(),
            totalFields: processedItems.length,
            fields: processedItems,
        };
        
        // Create downloadable JSON file
        const jsonString = JSON.stringify(dbGroup, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `dbgroup-${dbGroupName.trim()}-${Date.now()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        console.log('[DBGROUP] Exported DBGroup as JSON:', dbGroup);
        alert(`✅ DBGroup Exported!\n\nName: "${dbGroupName}"\nFields: ${processedItems.length}\n\nJSON file downloaded successfully!`);
    };

    return (
        <div>
            {mode === 'db' ? (
                // DB Mode: Select DBGroup and create DB instance
                <div>
                    <h3 style={language!=="ar"?{textAlign:"left"}:{textAlign:"right"}}>{TEXT.SelectDbGroupSection?.[language] || 'Select DBGroup Template'}</h3>
                    
                    {availableDbGroups.length === 0 ? (
                        <Typography variant="body1" color="error" style={{ marginBottom: '20px' }}>
                            {TEXT.NoDbGroupsAvailable?.[language] || 'No DBGroups available. Please create a DBGroup first.'}
                        </Typography>
                    ) : (
                        <FormControl fullWidth style={{ marginBottom: '20px' }}>
                            <InputLabel id="dbgroup-select-label">{TEXT.DbGroupTemplate?.[language] || 'DBGroup Template'}</InputLabel>
                            <Select
                                labelId="dbgroup-select-label"
                                value={selectedDbGroup?.id || ''}
                                onChange={(e) => {
                                    const selected = availableDbGroups.find(dbg => dbg.id === e.target.value);
                                    handleSelectDbGroup(selected);
                                }}
                            >
                                {availableDbGroups.map((dbGroup) => (
                                    <MenuItem key={dbGroup.id} value={dbGroup.id}>
                                        {dbGroup.name} ({dbGroup.totalFields} fields)
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    
                    {selectedDbGroup && (
                        <div>
                            <TextField
                                label={TEXT.DatabaseName?.[language] || "Database Name"}
                                variant="outlined"
                                fullWidth
                                required
                                value={dbName}
                                onChange={(e) => setDbName(e.target.value)}
                                placeholder={TEXT.EnterDatabaseName?.[language] || "Enter a name for your Database instance"}
                                helperText={TEXT.DatabaseNameHelper?.[language] || "This name identifies your database instance"}
                                error={!dbName && selectedDbGroup}
                                style={{ marginBottom: '20px' }}
                                InputProps={{
                                    style: { backgroundColor: theme.palette.background.main }
                                }}
                            />
                            
                            <Button
                                variant="contained"
                                color="success"
                                size="large"
                                onClick={handleCreateDb}
                                disabled={!dbName || dbName.trim() === ''}
                                style={{ fontWeight: 'bold', marginBottom: '20px' }}
                            >
                                {TEXT.CreateDatabase?.[language] || 'Create Database'}
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                // DBGroup Mode: Create new DBGroup template
                <TextField
                    label="DBGroup Name"
                    variant="outlined"
                    fullWidth
                    required
                    value={dbGroupName}
                    onChange={(e) => setDbGroupName(e.target.value)}
                    placeholder="Enter a unique name for your DBGroup"
                    helperText="This name will be used to identify your DBGroup"
                    error={!dbGroupName && items.length > 0}
                    InputProps={{
                        style: { backgroundColor: theme.palette.background.main }
                    }}
                />
            )}

            <h3 style={language!=="ar"?{textAlign:"left"}:{textAlign:"right"}}>
                {mode === 'db' ? (TEXT.FieldPreviewSection?.[language] || 'Field Preview') : TEXT.FieldTypeAddingSection[language]}
            </h3>
            {mode === 'dbgroup' && (
            <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <FormControl style={{ marginRight: '10px', minWidth: 120 }}>
                        <InputLabel id="field-type-label">Field Type</InputLabel>
                        <Select
                            labelId="field-type-label"
                            value={fieldType}
                            onChange={(e) => {
                                setFieldType(e.target.value);
                                const selected = fieldTypes.find((type) => type.type === e.target.value);
                                setFieldName(selected?.defaultName || '');
                            }}
                            variant="standard"
                        >
                            {fieldTypes.map((type) => (
                                <MenuItem key={type.type} value={type.type}>{type.type}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Field Name"
                        variant="standard"
                        value={fieldName}
                        onChange={(e) => setFieldName(e.target.value)}
                        style={{ marginRight: '10px' }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddItem}
                    >
                        Add Field
                    </Button>
                </div>
                {renderOptions()}
                {renderConstraints()}
            </div>
            )}

            {/* Added Fields Section */}
            <h3 style={language!=="ar"?{textAlign:"left"}:{textAlign:"right"}}>{TEXT.FieldOrganizationAndPreviewSection[language]}</h3>
            
            {/* Create DbGroup Button */}
            {mode === 'dbgroup' && items.length > 0 && (
                <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px', alignItems: 'center' }}>
                    {!dbGroupName && (
                        <Typography variant="caption" color="error" style={{ fontWeight: 'bold' }}>
                            Please enter a DBGroup name to create
                        </Typography>
                    )}
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => console.log("Current items state:", JSON.stringify(items, null, 2))}
                    >
                        Debug Items
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handleExportDbGroupJSON}
                        disabled={!dbGroupName || dbGroupName.trim() === ''}
                        style={{ fontWeight: 'bold' }}
                    >
                        Export as JSON
                    </Button>
                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        onClick={handleCreateDbGroup}
                        disabled={!dbGroupName || dbGroupName.trim() === ''}
                        style={{ fontWeight: 'bold' }}
                    >
                        Create DBGroup ({items.length} fields)
                    </Button>
                </div>
            )}
            
            <div style={{ marginTop: '20px', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>
            <DragDropContext onDragEnd={handleDragEnd} style={{ border: '1px solid #ccc', borderRadius: '4px'}}>
                <Droppable droppableId="droppable" key={items.length}>
                    {(provided) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={{
                                listStyleType: 'none',
                                padding: 0,
                                minHeight: '50px',
                            }}
                        >
                            {items.map((item, index) => (
                                <Draggable
                                    key={item.name}
                                    draggableId={item.name}
                                    index={index}
                                    isDragDisabled={dragDisabled} // Disable dragging when `dragDisabled` is true
                                >
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={{
                                                padding: '8px',
                                                margin: '4px 0',
                                                backgroundColor: '#eeeeeeff',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                ...provided.draggableProps.style,
                                            }}
                                            onClick={() => handleFieldClick(item)}
                                            >
                                                {fieldSelector(item, items, setItems, get_item_by_id, get_item_index_by_id, setDragDisabled)}
                                            {mode === 'dbgroup' && (
                                                <button
                                                    onClick={() => handleRemoveItem(index)}
                                                    style={{
                                                        background: 'red',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        padding: '4px 8px',
                                                        marginLeft: '10px',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    X
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
                    </div>
        </div>
    );
};

// Export with both names for compatibility
export default DbCreator;
export const CreateDbGroup = DbCreator;